import { useCallback, useEffect, useMemo, useState } from "react";

import { type AWSNodeConfigApiParameters, type CloudFlowNodeType } from "@doitintl/cmp-models";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  FormControl,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import noop from "lodash/noop";
import set from "lodash/set";

import { FormChangesListener } from "../../../Common/FormChangesListener";
import { useNodeConfigurationContext } from "../../NodeConfigurationContext";
import { useInitialErrors } from "../hooks";
import { waiterConfigValidationSchema } from "./schema";
import { type WaiterAutocompleteValue } from "./useAWSWaiters";
import { WaiterPermissionsListener } from "./WaiterPermissionsListener";

const waiterFields = [
  {
    key: "minDelay",
    label: "Min Delay (seconds)",
  },
  {
    key: "maxDelay",
    label: "Max Delay (seconds)",
  },
  {
    key: "maxWaitTime",
    label: "Max Wait Time (seconds)",
  },
];

type WaiterConfigOverridesProps = {
  parameters: AWSNodeConfigApiParameters;
  waiters: WaiterAutocompleteValue[];
  selectedWaiter: WaiterAutocompleteValue | null;
  setIsWaiterValid: (isValid: boolean) => void;
};

export const WaiterConfigOverrides = ({
  parameters,
  waiters,
  selectedWaiter,
  setIsWaiterValid,
}: WaiterConfigOverridesProps) => {
  const { updateNode } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const [accordionOpen, setAccordionOpen] = useState(false);

  const initialValues = useMemo(
    () => waiterConfigValidationSchema.cast(parameters.waiterConfiguration ?? {}),
    [parameters.waiterConfiguration]
  );

  useEffect(() => {
    setIsWaiterValid(waiterConfigValidationSchema.isValidSync(initialValues));
  }, [initialValues, setIsWaiterValid]);

  const onWaiterConfigurationChange = useCallback(
    (values) => {
      const waiterConfiguration = waiterConfigValidationSchema.cast(values, {
        context: {
          castingPhase: "outgoing",
        },
      });

      updateNode((prev) => {
        // Override parameters of prev waiter configuration if it's changed
        const prevWaiter = (prev.parameters as AWSNodeConfigApiParameters).waiterConfiguration?.waiter;
        if (waiterConfiguration.waiter !== prevWaiter) {
          return set(prev, "parameters.waiterConfiguration", {
            waiter: waiterConfiguration.waiter,
          });
        }
        return set(prev, "parameters.waiterConfiguration", waiterConfiguration);
      });
    },
    [updateNode]
  );

  const initialErrors = useInitialErrors(waiterConfigValidationSchema, initialValues);

  if (!waiters || waiters.length === 0) {
    return null;
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={waiterConfigValidationSchema}
      onSubmit={noop}
      validateOnMount
      validateOnBlur
      validateOnChange
      initialErrors={initialErrors}
    >
      {({ setFieldValue, errors }) => (
        <Form>
          <Stack spacing={2}>
            <FormControl>
              <Autocomplete<WaiterAutocompleteValue>
                options={waiters}
                getOptionLabel={(option) => option.id}
                renderOption={(props, option) => (
                  <MenuItem {...props} key={option.id} value={option.id}>
                    {option.id}
                  </MenuItem>
                )}
                onChange={(_, value) => {
                  setFieldValue("waiter", value?.id);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Wait until"
                    required
                    fullWidth
                    variant="outlined"
                    error={Boolean(errors.waiter)}
                    helperText={errors.waiter ? String(errors.waiter) : undefined}
                  />
                )}
                size="small"
                fullWidth
                value={selectedWaiter}
              />
            </FormControl>

            {selectedWaiter && (
              <Accordion
                elevation={0}
                disableGutters
                expanded={accordionOpen}
                onChange={(_, expanded) => {
                  setAccordionOpen(expanded);
                }}
                sx={{
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="waiter-config-overrides-content"
                  id="waiter-config-overrides-header"
                >
                  <Typography variant="subtitle2">Override waiter configuration</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {waiterFields.map(({ key, label }) => (
                      <TextField
                        key={key}
                        type="number"
                        label={label}
                        name={`waiterConfigOverrides.${key}`}
                        value={parameters.waiterConfiguration?.waiterConfigOverrides?.[key] ?? ""}
                        onChange={(e) => setFieldValue(`waiterConfigOverrides.${key}`, Number(e.target.value))}
                        error={Boolean(errors.waiterConfigOverrides?.[key])}
                        helperText={
                          errors.waiterConfigOverrides?.[key] ? String(errors.waiterConfigOverrides?.[key]) : undefined
                        }
                        fullWidth
                      />
                    ))}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
          {selectedWaiter && <WaiterPermissionsListener parameters={parameters} selectedWaiter={selectedWaiter} />}
          <FormChangesListener onValidityChange={setIsWaiterValid} onValuesChange={onWaiterConfigurationChange} />
        </Form>
      )}
    </Formik>
  );
};
